import { useEffect, useState, type FormEvent } from 'react';

import { AuthenticatorSecret } from '@/components/dashboard/AuthenticatorSecret';
import { initials } from '@/components/dashboard/types';
import { api, ApiError } from '@/lib/api';

interface OwnAccount {
  name: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  emailVerified: boolean;
  phone: string | null;
  phoneVerified: boolean;
  pendingPhone: string | null;
  locale: string;
  timezone: string | null;
  avatarUrl: string | null;
  role: string;
  twoFactorEnabled: boolean;
  scope: string;
}

interface PhoneChallenge {
  challengeId: string;
  phoneMask?: string;
  demoCode?: string;
}

const fieldClass = 'mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand';

type AccountSection = 'details' | 'contact' | 'password' | 'authenticator';

function roleLabel(role: string): string {
  if (role === 'platform_admin') return 'Platform admin';
  if (role === 'super_admin') return 'Agency owner';
  if (role === 'front_desk') return 'Front desk';
  return role;
}

export function OwnProfile({ platform }: { platform: boolean }) {
  const base = platform ? '/api/platform/auth' : '/api/auth';
  const [account, setAccount] = useState<OwnAccount | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState('fr');
  const [timezone, setTimezone] = useState('Africa/Casablanca');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneChallenge, setPhoneChallenge] = useState<PhoneChallenge | null>(null);
  const [factorSecret, setFactorSecret] = useState<string | null>(null);
  const [factorUri, setFactorUri] = useState<string | null>(null);
  const [factorCode, setFactorCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [emailDemo, setEmailDemo] = useState<string | null>(null);
  const [emailPrompt, setEmailPrompt] = useState(false);
  const [turnOffPassword, setTurnOffPassword] = useState('');
  const [turnOffCode, setTurnOffCode] = useState('');
  const [showTurnOff, setShowTurnOff] = useState(false);
  const [section, setSection] = useState<AccountSection>('details');

  useEffect(() => {
    let cancelled = false;
    api<{ user: OwnAccount }>(`${base}/me`)
      .then((payload) => {
        if (cancelled) return;
        applyAccount(payload.user);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your profile');
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  function applyAccount(next: OwnAccount) {
    setAccount(next);
    setFirstName(next.firstName ?? '');
    setLastName(next.lastName ?? '');
    setPhone(next.phone ?? '');
    setLocale(next.locale || 'fr');
    setTimezone(next.timezone ?? 'Africa/Casablanca');
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const payload = await api<{ user: OwnAccount }>(`${base}/me`, {
        method: 'PATCH',
        body: JSON.stringify({
          firstName,
          lastName,
          name: `${firstName.trim()} ${lastName.trim()}`.trim() || account?.name,
          locale,
          timezone,
        }),
      });
      applyAccount(payload.user);
      setNotice('Profile saved.');
    }, 'Could not save your profile');
  }

  async function verifyEmail() {
    await run(async () => {
      const result = await api<{ demoCode?: string }>(`${base}/me/email-otp`, { method: 'POST', body: '{}' });
      setEmailDemo(result.demoCode ?? null);
      setEmailPrompt(true);
      setEmailCode('');
      setNotice(`A 6-digit code was sent to ${account?.email ?? 'your email'}. The address stays the same.`);
    }, 'Could not send the verification email');
  }

  async function confirmEmail(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const result = await api<{ user: OwnAccount }>(`${base}/me/email-otp/confirm`, {
        method: 'POST',
        body: JSON.stringify({ code: emailCode.trim() }),
      });
      applyAccount(result.user);
      setEmailCode('');
      setEmailDemo(null);
      setEmailPrompt(false);
      setNotice('Email verified. The address did not change.');
    }, 'That code is not valid');
  }

  async function sendPhone(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const data = await api<PhoneChallenge & { user?: OwnAccount }>(`${base}/me/phone`, {
        method: 'POST',
        body: JSON.stringify({ phone: phone.trim() }),
      });
      if (typeof data.challengeId === 'string') {
        setPhoneChallenge(data);
        setPhoneCode('');
        setNotice(`Enter the code sent to ${data.phoneMask ?? 'your phone'}.`);
        return;
      }
      if (!data.user) throw new Error('Could not verify that phone number');
      applyAccount(data.user);
      setPhoneChallenge(null);
      setNotice('Phone number verified.');
    }, 'Could not verify that phone number');
  }

  async function confirmPhone(event: FormEvent) {
    event.preventDefault();
    if (!phoneChallenge) return;
    await run(async () => {
      const payload = await api<{ user: OwnAccount }>(`${base}/me/phone/verify`, {
        method: 'POST',
        body: JSON.stringify({ challengeId: phoneChallenge.challengeId, code: phoneCode.trim() }),
      });
      applyAccount(payload.user);
      setPhoneChallenge(null);
      setPhoneCode('');
      setNotice('Phone number verified.');
    }, 'That code is not valid');
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    if (password !== passwordConfirmation) {
      setError('The new passwords do not match.');
      setNotice(null);
      return;
    }
    await run(async () => {
      const payload = await api<{ user: OwnAccount }>(`${base}/me/password`, {
        method: 'POST',
        body: JSON.stringify({ currentPassword, password, password_confirmation: passwordConfirmation }),
      });
      applyAccount(payload.user);
      setCurrentPassword('');
      setPassword('');
      setPasswordConfirmation('');
      setNotice('Password changed.');
    }, 'Could not change the password');
  }

  async function startTwoFactor(event?: FormEvent) {
    event?.preventDefault();
    await run(async () => {
      const replacing = account?.twoFactorEnabled === true;
      const result = await api<{ secret: string; otpauthUri: string }>(`${base}/me/two-factor`, {
        method: 'POST',
        body: JSON.stringify(replacing
          ? { currentPassword: disablePassword, code: factorCode.trim() }
          : {}),
      });
      setFactorSecret(result.secret);
      setFactorUri(result.otpauthUri);
      setFactorCode('');
      setDisablePassword('');
      setNotice(replacing
        ? 'Scan the new QR code. The current authenticator keeps working until you confirm the new code.'
        : 'Scan the QR code or enter the secret, then confirm with a 6-digit code.');
    }, 'Could not start two-factor setup');
  }

  async function confirmTwoFactor(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const payload = await api<{ user: OwnAccount }>(`${base}/me/two-factor/confirm`, {
        method: 'POST',
        body: JSON.stringify({ code: factorCode.trim() }),
      });
      applyAccount(payload.user);
      setFactorSecret(null);
      setFactorUri(null);
      setFactorCode('');
      setNotice('Two-factor authentication is on. The next sign-in asks for a code.');
    }, 'That code is not valid');
  }

  async function disableTwoFactor(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const payload = await api<{ user: OwnAccount }>(`${base}/me/two-factor`, {
        method: 'DELETE',
        body: JSON.stringify({ currentPassword: turnOffPassword, code: turnOffCode.trim() }),
      });
      applyAccount(payload.user);
      setTurnOffPassword('');
      setTurnOffCode('');
      setShowTurnOff(false);
      setNotice('Two-factor authentication is off.');
    }, 'Could not turn off two-factor authentication');
  }

  async function uploadAvatar(file: File | null) {
    await run(async () => {
      if (file === null) {
        const payload = await api<{ user: OwnAccount }>(`${base}/avatar`, { method: 'DELETE' });
        applyAccount(payload.user);
        return;
      }
      const body = new FormData();
      body.set('avatar', file);
      const response = await fetch(`${base}/avatar`, { method: 'POST', body });
      const data = (await response.json().catch(() => ({}))) as { user?: OwnAccount; error?: string };
      if (!response.ok || !data.user) throw new Error(data.error ?? 'Choose a JPEG, PNG, or WebP image up to 2 MB.');
      applyAccount(data.user);
    }, 'Could not update the photo');
  }

  async function run(work: () => Promise<void>, fallback: string) {
    if (pending) return;
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      await work();
    } catch (err: unknown) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : fallback);
    } finally {
      setPending(false);
    }
  }

  const label = account ? initials(account.name, account.email) : '…';
  const emailOk = account?.emailVerified === true;
  const phoneOk = account?.phoneVerified === true;
  const contactDetail = emailOk && phoneOk
    ? 'Verified'
    : !emailOk && !phoneOk
      ? 'Needs a code'
      : !emailOk
        ? 'Email needs a code'
        : 'Phone needs a code';
  const statuses: Array<{ id: AccountSection; label: string; ok: boolean; detail: string }> = [
    { id: 'contact', label: 'Email & phone', ok: emailOk && phoneOk, detail: contactDetail },
    { id: 'authenticator', label: 'Authenticator', ok: account?.twoFactorEnabled === true, detail: account?.twoFactorEnabled ? 'On' : (platform ? 'Required' : 'Off') },
    { id: 'password', label: 'Password', ok: true, detail: 'Change' },
  ];

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <section className="rounded-2xl border border-line bg-card p-5">
        <div className="flex flex-wrap items-center gap-4">
          {account?.avatarUrl ? (
            <img src={account.avatarUrl} alt="" className="h-16 w-16 rounded-2xl object-cover" />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand text-sm font-bold text-slate-950">{label}</span>
          )}
          <div className="min-w-0">
            <h2 className="truncate text-base font-bold text-ink">{account?.name ?? 'My profile'}</h2>
            <p className="truncate text-xs text-muted">{account?.email}</p>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-muted">{account ? roleLabel(account.role) : ''}</p>
            <button type="button" onClick={() => setSection('details')} className={`mt-2 text-xs font-semibold ${section === 'details' ? 'text-brand' : 'text-ink'}`}>Edit details</button>
          </div>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {statuses.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSection(item.id)}
              className={`rounded-xl border px-3 py-2 text-left ${section === item.id ? 'border-brand bg-shell' : 'border-line hover:bg-shell'}`}
            >
              <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted">{item.label}</span>
              <span className={`mt-1 block text-xs font-semibold ${item.id === 'password' ? 'text-ink' : (item.ok ? 'text-success' : 'text-warning')}`}>{item.detail}</span>
            </button>
          ))}
        </div>
        {error && <p className="mt-3 text-xs font-semibold text-danger" role="alert">{error}</p>}
        {notice && <p className="mt-3 text-xs font-semibold text-success" role="status">{notice}</p>}
      </section>

      <section className="rounded-2xl border border-line bg-card p-5">
          {section === 'details' && (
            <form onSubmit={(event) => void save(event)} className="space-y-3">
              <h3 className="text-sm font-bold text-ink">Personal details</h3>
              <p className="text-xs text-muted">Name, language, and photo. Email and phone are verified together.</p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs font-medium text-muted">
                  Photo
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="mt-1 block text-xs" onChange={(event) => void uploadAvatar(event.target.files?.[0] ?? null)} />
                </label>
                {account?.avatarUrl && (
                  <button type="button" disabled={pending} onClick={() => void uploadAvatar(null)} className="text-xs font-semibold text-danger">Remove photo</button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs font-medium text-muted">First name<input value={firstName} onChange={(event) => setFirstName(event.target.value)} className={fieldClass} /></label>
                <label className="block text-xs font-medium text-muted">Last name<input value={lastName} onChange={(event) => setLastName(event.target.value)} className={fieldClass} /></label>
                <label className="block text-xs font-medium text-muted">Language
                  <select value={locale} onChange={(event) => setLocale(event.target.value)} className={fieldClass}>
                    <option value="fr">French</option>
                    <option value="en">English</option>
                    <option value="ar">Arabic</option>
                    <option value="es">Spanish</option>
                  </select>
                </label>
                <label className="block text-xs font-medium text-muted">Timezone<input value={timezone} onChange={(event) => setTimezone(event.target.value)} className={fieldClass} /></label>
              </div>
              <button type="submit" disabled={pending} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">Save profile</button>
            </form>
          )}

          {section === 'contact' && (
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <h3 className="text-sm font-bold text-ink">Email</h3>
                <p className="text-sm text-ink">{account?.email}</p>
                <p className={`text-xs font-semibold ${account?.emailVerified ? 'text-success' : 'text-warning'}`}>{account?.emailVerified ? 'Verified' : 'Not verified'}</p>
                <p className="text-xs text-muted">The address stays on this account. A 6-digit code confirms you can open it.</p>
                <button type="button" disabled={pending || !account} onClick={() => void verifyEmail()} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">Send email code</button>
                {emailPrompt && (
                  <form onSubmit={confirmEmail} className="flex flex-wrap items-end gap-2">
                    <label className="text-xs font-medium text-muted">Email code
                      <input inputMode="numeric" maxLength={6} value={emailCode} onChange={(event) => setEmailCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" className={`${fieldClass} max-w-[10rem]`} />
                    </label>
                    <button type="submit" disabled={pending || emailCode.length !== 6} className="rounded-full bg-brand px-3 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">Verify code</button>
                  </form>
                )}
                {emailDemo && <p className="text-xs font-semibold text-warning">Demo code {emailDemo}</p>}
              </div>
              <div className="space-y-3 border-t border-line pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
                <h3 className="text-sm font-bold text-ink">Phone</h3>
                <p className={`text-xs font-semibold ${account?.phoneVerified ? 'text-success' : 'text-warning'}`}>
                  {account?.phoneVerified ? 'Verified' : 'Not verified'}
                  {account?.pendingPhone ? ` · pending ${account.pendingPhone}` : ''}
                </p>
                <p className="text-xs text-muted">A Moroccan number is confirmed by SMS before it replaces the current one.</p>
                <form onSubmit={(event) => void sendPhone(event)} className="space-y-2">
                  <label className="block text-xs font-medium text-muted">Moroccan number
                    <input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+212…" className={fieldClass} />
                  </label>
                  <button type="submit" disabled={pending || phone.trim() === ''} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">Send SMS code</button>
                </form>
                {phoneChallenge && (
                  <form onSubmit={(event) => void confirmPhone(event)} className="space-y-2">
                    <p className="text-xs text-muted">Code for {phoneChallenge.phoneMask}.{phoneChallenge.demoCode ? ` Demo code ${phoneChallenge.demoCode}` : ''}</p>
                    <input value={phoneCode} onChange={(event) => setPhoneCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className={fieldClass} />
                    <button type="submit" disabled={pending || phoneCode.length !== 6} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">Verify phone</button>
                  </form>
                )}
              </div>
            </div>
          )}

          {section === 'password' && (
            <form onSubmit={(event) => void changePassword(event)} className="grid gap-3 sm:grid-cols-2">
              <h3 className="text-sm font-bold text-ink sm:col-span-2">Password</h3>
              <p className="text-xs text-muted sm:col-span-2">At least 12 characters. This does not turn off the authenticator. If you forgot the current password, use Forgot password on the sign-in screen.</p>
              <label className="block text-xs font-medium text-muted sm:col-span-2">Current password<input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" className={fieldClass} /></label>
              <label className="block text-xs font-medium text-muted">New password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={12} autoComplete="new-password" className={fieldClass} /></label>
              <label className="block text-xs font-medium text-muted">Confirm new password<input type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} minLength={12} autoComplete="new-password" className={fieldClass} /></label>
              {password !== '' && passwordConfirmation !== '' && password !== passwordConfirmation && (
                <p className="text-xs font-semibold text-danger sm:col-span-2">The new passwords do not match.</p>
              )}
              <button type="submit" disabled={pending || password.length < 12 || password !== passwordConfirmation} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50 sm:col-span-2 sm:w-fit">Change password</button>
            </form>
          )}

          {section === 'authenticator' && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-ink">Authenticator</h3>
              <p className={`text-xs font-semibold ${account?.twoFactorEnabled ? 'text-success' : 'text-warning'}`}>{account?.twoFactorEnabled ? 'On' : 'Off'}</p>
              <p className="text-xs text-muted">
                {platform
                  ? 'Every platform sign-in asks for a code. Reset replaces the device. The old authenticator keeps working until the new code is confirmed.'
                  : 'Optional for this agency. When it is on, sign-in asks for a 6-digit code.'}
              </p>
              <p className="rounded-xl border border-line bg-shell px-3 py-2 text-xs text-muted">{`Lost the phone? Sign out, enter your password, then choose “I lost my authenticator.” The recovery code is emailed to ${account?.email ?? 'this account'}.`}</p>
              {factorSecret && factorUri ? (
                <form onSubmit={(event) => void confirmTwoFactor(event)} className="space-y-3">
                  <AuthenticatorSecret secret={factorSecret} otpauthUri={factorUri} variant="desk" />
                  <label className="block text-xs font-medium text-muted">Code from the new authenticator
                    <input value={factorCode} onChange={(event) => setFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" className={fieldClass} />
                  </label>
                  <button type="submit" disabled={pending || factorCode.length !== 6} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">Confirm authenticator</button>
                </form>
              ) : account?.twoFactorEnabled ? (
                <div className="space-y-4">
                  <form onSubmit={(event) => void startTwoFactor(event)} className="space-y-2">
                    <label className="block text-xs font-medium text-muted">Current password<input type="password" value={disablePassword} onChange={(event) => setDisablePassword(event.target.value)} autoComplete="current-password" className={fieldClass} /></label>
                    <label className="block text-xs font-medium text-muted">Current authenticator code<input value={factorCode} onChange={(event) => setFactorCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" className={fieldClass} /></label>
                    <button type="submit" disabled={pending || disablePassword === '' || factorCode.length !== 6} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink disabled:opacity-50">Reset authenticator</button>
                  </form>
                  {!platform && (
                    <div className="border-t border-line pt-3">
                      {showTurnOff ? (
                        <form onSubmit={(event) => void disableTwoFactor(event)} className="space-y-2">
                          <p className="text-xs text-muted">Turning it off removes the code from the next sign-in.</p>
                          <label className="block text-xs font-medium text-muted">Current password<input type="password" value={turnOffPassword} onChange={(event) => setTurnOffPassword(event.target.value)} autoComplete="current-password" className={fieldClass} /></label>
                          <label className="block text-xs font-medium text-muted">Current authenticator code<input value={turnOffCode} onChange={(event) => setTurnOffCode(event.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" className={fieldClass} /></label>
                          <div className="flex gap-2">
                            <button type="submit" disabled={pending || turnOffPassword === '' || turnOffCode.length !== 6} className="rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-danger disabled:opacity-50">Turn off</button>
                            <button type="button" onClick={() => setShowTurnOff(false)} className="text-xs font-semibold text-muted">Cancel</button>
                          </div>
                        </form>
                      ) : (
                        <button type="button" onClick={() => setShowTurnOff(true)} className="text-xs font-semibold text-danger">Turn off authenticator</button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <button type="button" disabled={pending} onClick={() => void startTwoFactor()} className="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">Set up authenticator</button>
              )}
            </div>
          )}
      </section>
    </div>
  );
}
